import { Container, ContainerOptions, Text, TextStyle } from "pixi.js";
import gsap from "gsap";
import GameConfig, { ColumnAlignment } from "../GameConfig";
import i18next from "i18next";
import GlobalStore from "../services/GlobalStore";
import { CommonEvents } from "../events/EventEnums";

export type HistoryEntry = {
  value: number;
  time: Date;
  location: string;
};

class JackpotHistory extends Container {
  private history: HistoryEntry[] = [];
  private titleValue: Text | undefined;
  private titleTime: Text | undefined;
  private textFieldsValue: Text[] = [];
  private textFieldsTime: Text[] = [];
  private textFieldsLocation: Text[] = [];
  private numOfEntries: number = 10;

  private showTimeline: gsap.core.Timeline | undefined;
  private hideTimeline: gsap.core.Timeline | undefined;

  constructor(containerOptions?: ContainerOptions) {
    super(containerOptions);

    const config = GameConfig.getInstance().config.history;
    const fontFamily: string = "Avenir Next Condensed Demi Bold";
    this.numOfEntries = config.numrows;
    this.visible = false;

    console.log("History config:", config);

    // Prepare positions and anchors for left and right aligned texts
    let value_anchorX = 0;
    if (config.rows.win.align == ColumnAlignment.RIGHT) {
      value_anchorX = 1;
    }

    let location_anchorX = 0;
    if (config.rows.location.align == ColumnAlignment.RIGHT) {
      location_anchorX = 1;
    }

    let time_anchorX = 0;
    if (config.rows.date.align == ColumnAlignment.RIGHT) {
      time_anchorX = 1;
    }

    // Setup titles
    if (config.header) {
      const headerColor: number = parseInt(config.header.color, 16);

      const titleStyleValue: TextStyle = new TextStyle({
        fontFamily: fontFamily,
        fontSize: config.header.font_size,
        fill: headerColor,
        align: config.rows.win.align,
      });

      const titleStyleTime: TextStyle = new TextStyle({
        fontFamily: fontFamily,
        fontSize: config.header.font_size,
        fill: headerColor,
        align: config.rows.date.align,
      });

      this.titleValue = new Text({
        text: i18next.t("common.title_value"),
        style: titleStyleValue,
      });
      this.titleValue.x = config.rows.win.x;
      this.titleValue.y = config.header.y;
      this.titleValue.anchor.set(value_anchorX, 0.5);
      this.titleValue.alpha = 0;
      this.addChild(this.titleValue);

      this.titleTime = new Text({
        text: i18next.t("common.title_time"),
        style: titleStyleTime,
      });
      this.titleTime.x = config.rows.date.x;
      this.titleTime.y = config.header.y;
      this.titleTime.anchor.set(time_anchorX, 0.5);
      this.titleTime.alpha = 0;
      this.addChild(this.titleTime);
    }

    // setup style
    for (let i = 0; i < this.numOfEntries; i++) {
      console.log(
        "From config:",
        config.rows.start_y,
        i,
        config.rows.spacing_y,
        config.rows.start_y + i * config.rows.spacing_y,
      );

      const yRow = config.rows.start_y + i * config.rows.spacing_y;
      {
        // Win
        const { x, y, size, color, align } = config.rows.win;
        const text = new Text({
          text: "",
          style: {
            fontFamily: "Avenir Next Condensed Demi Bold",
            fontSize: size,
            fill: color,
            align: align,
          },
        });

        text.alpha = 0;
        text.anchor.set(align === "right" ? 1 : 0, 1);
        text.x = x;
        text.y = yRow + y;
        this.addChild(text);

        this.textFieldsValue.push(text);
      }
      {
        // Location
        const { x, y, size, color, align } = config.rows.location;
        const text = new Text({
          text: "",
          style: {
            fontFamily: "Avenir Next Condensed Demi Bold",
            fontSize: size,
            fill: color,
            align: align,
          },
        });

        text.alpha = 0;
        text.anchor.set(align === "right" ? 1 : 0, 1);
        text.x = x;
        text.y = yRow + y;
        this.addChild(text);

        this.textFieldsLocation.push(text);
        console.log("Location text, ", text.x, text.y);
      }
      {
        // Time
        const { x, y, size, color, align } = config.rows.date;
        const text = new Text({
          text: "",
          style: {
            fontFamily: "Avenir Next Condensed Demi Bold",
            fontSize: size,
            fill: color,
            align: align,
          },
        });

        text.alpha = 0;
        text.anchor.set(align === "right" ? 1 : 0, 1);
        text.x = x;
        text.y = yRow + y;
        this.addChild(text);

        this.textFieldsTime.push(text);
      }
    }

    this.addEventListeners();
  }

  add = (entry: HistoryEntry | HistoryEntry[]): void => {
    if (Array.isArray(entry)) {
      this.history.unshift(...entry);
    } else {
      this.history.unshift(entry);
    }

    this.history.sort((a, b) => {
      return b.time.getTime() - a.time.getTime();
    });

    if (this.history.length) {
      this.history.splice(this.numOfEntries);
    }
  };

  clear = (): void => {
    this.history.splice(0, this.history.length);
  };

  update = (): void => {
    const formatter = new Intl.NumberFormat(i18next.language, {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    for (let i = 0; i < this.numOfEntries; i++) {
      if (i < this.history.length) {
        this.textFieldsValue[i].text =
          `${formatter.format(this.history[i].value)}`;
        this.textFieldsLocation[i].text = `${this.history[i].location}`;
        this.textFieldsTime[i].text =
          `${this.history[i].time.toLocaleDateString(i18next.language)}`;
      } else {
        this.textFieldsValue[i].text = "";
        this.textFieldsLocation[i].text = "";
        this.textFieldsTime[i].text = "";
      }

      console.log(this.textFieldsValue[i].text, this.history[i]);
    }
  };

  show = (delay: number = 0): void => {
    this.visible = true;
    console.log("JackpotHistory showing with delay", delay);

    if (this.hideTimeline) {
      this.hideTimeline.pause(0);
    }

    if (!this.showTimeline) {
      this.showTimeline = gsap.timeline();

      const optsStart = {
        alpha: 0,
      };

      const opts = {
        duration: 0.5,
        stagger: 0.0,
        delay: delay,
        alpha: 1,
      };

      console.log("JackpotHistory creating show timeline with options", opts);

      this.showTimeline.add([
        // gsap.fromTo(this.titleTime, optsStart, opts),
        // gsap.fromTo(this.titleValue, optsStart, opts),
        gsap.fromTo(this.textFieldsTime, optsStart, opts),
        gsap.fromTo(this.textFieldsLocation, optsStart, opts),
        gsap.fromTo(this.textFieldsValue, optsStart, opts),
      ]);
    }

    this.showTimeline.play(0);
  };

  hide = (delay: number = 0): void => {
    console.log("JackpotHistory hiding with delay", delay);

    if (!this.hideTimeline) {
      this.hideTimeline = gsap.timeline();

      const optsStart = {
        alpha: 1,
      };

      const opts = {
        duration: 0.5,
        stagger: 0.1,
        delay: Math.max(0, delay - 0.5),
        alpha: 0,
      };

      console.log("JackpotHistory creating hide timeline with options", opts);

      this.hideTimeline.add([
        // gsap.fromTo(this.titleTime, optsStart, opts),
        // gsap.fromTo(this.titleValue, optsStart, opts),
        gsap.fromTo(this.textFieldsTime, optsStart, opts),
        gsap.fromTo(this.textFieldsLocation, optsStart, opts),
        gsap.fromTo(this.textFieldsValue, optsStart, {
          ...opts,
          onComplete: () => {
            this.visible = false;
          },
        }),
      ]);
    }

    this.hideTimeline.play(0);
  };

  hideInstant = (): void => {
    this.visible = false;
    this.showTimeline?.pause(0);
    this.hideTimeline?.pause(0);

    for (const text of [
      ...this.textFieldsTime,
      ...this.textFieldsLocation,
      ...this.textFieldsValue,
    ]) {
      text.alpha = 0;
    }
  };

  private addEventListeners = () => {
    const commonEE = GlobalStore.commonEventEmitter;

    commonEE.on(CommonEvents.CHANGE_LANGUAGE, this.updateLanguage);
  };

  private removeEventListeners = () => {
    const commonEE = GlobalStore.commonEventEmitter;

    commonEE.off(CommonEvents.CHANGE_LANGUAGE, this.updateLanguage);
  };

  private updateLanguage = (): void => {
    if (this.titleValue) this.titleValue.text = i18next.t("common.title_value");

    if (this.titleTime) this.titleTime.text = i18next.t("common.title_time");
  };
}

export default JackpotHistory;
